import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { t } from '../i18n';
import { DatabaseRuleError, type ConsoleApi } from '../lib/api';
import { formatDate } from '../places/format';
import { hrefFor } from '../shell/route';
import { controlClass } from '../styles';
import { Button, Card, Field, FormFooter, Notice } from '../ui';
import { errorText } from '../lib/errors';
import { menuLabel } from './format';

/** The menus of a place, and a field to add one by its URL. */
export function MenusCard({
  api,
  placeId,
  canEdit,
}: {
  api: ConsoleApi;
  placeId: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const menus = useQuery({ queryKey: ['menus', placeId], queryFn: () => api.listMenus(placeId) });
  const [url, setUrl] = useState('');
  const add = useMutation({
    mutationFn: (value: string) => api.addMenu(placeId, value),
    onSuccess: () => {
      setUrl('');
      void queryClient.invalidateQueries({ queryKey: ['menus', placeId] });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    add.mutate(url);
  }
  // Only a refused address marks the field; a network failure is not the field's fault.
  const refused = add.error instanceof DatabaseRuleError;

  return (
    <Card title={t('menus.title')}>
      {menus.isPending ? (
        <p role="status" className="text-muted">
          {t('menus.loading')}
        </p>
      ) : null}
      {menus.isError ? (
        <div role="alert" className="grid justify-items-start gap-3">
          <p>{t('menus.error')}</p>
          <Button onClick={() => void menus.refetch()}>{t('common.retry')}</Button>
        </div>
      ) : null}
      {menus.isSuccess && menus.data.length === 0 ? (
        <p className="text-sm text-muted">{t('menus.empty')}</p>
      ) : null}
      {menus.isSuccess && menus.data.length > 0 ? (
        <ul className="-my-2 grid divide-y divide-line">
          {menus.data.map((menu) => (
            <li key={menu.id} className="py-1">
              {/* One target for the whole entry: its address, then its state in words. */}
              <a
                href={hrefFor({ name: 'menu', placeId, menuId: menu.id })}
                className="group grid min-h-11 gap-0.5 rounded-lg py-2"
              >
                <span className="text-[14.5px] font-semibold break-all underline-offset-4 group-hover:underline">
                  {menuLabel(menu)}
                </span>
                <span className="text-[13px] text-muted">
                  {[
                    t(`menuFormat.${menu.format}`),
                    t(`menuStatus.${menu.status}`),
                    menu.lastReadAt
                      ? t('menus.lastRead', { date: formatDate(menu.lastReadAt) })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      {canEdit ? (
        <form className="grid gap-5 border-t border-line pt-5" onSubmit={submit} noValidate>
          <Field
            label={t('menus.url')}
            htmlFor="menu-url"
            hint={t('menus.urlHint')}
            hintId="menu-url-hint"
          >
            <input
              id="menu-url"
              type="url"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              placeholder="https://"
              value={url}
              onChange={(event) => {
                add.reset();
                setUrl(event.target.value);
              }}
              aria-invalid={refused}
              aria-describedby={refused ? 'menu-url-hint menu-url-error' : 'menu-url-hint'}
              className={controlClass}
            />
          </Field>
          <FormFooter
            status={
              add.isError ? <Notice id="menu-url-error">{errorText(add.error)}</Notice> : null
            }
            live={add.isSuccess ? t('menus.added') : ''}
          >
            <Button type="submit" disabled={add.isPending || !url.trim()}>
              {add.isPending ? t('menus.adding') : t('menus.add')}
            </Button>
          </FormFooter>
        </form>
      ) : null}
    </Card>
  );
}
